// Piano Keyboard Display Component

import React, { useState, useEffect, useRef } from 'react';
import { getSpellings, midiToName, midiToScaleDegree, midiToRomanNumeral, midiToScaleDegreeNumber, midiToScaleDegreeExtension, midiToNashvilleNumber, midiToSingleNote, getOvertoneInfo, getModeFromPcSet } from '../utils';
import * as Tone from 'tone';
import { useColors, computeShadowColor, computeBlackKeyColor, computeBlackKeyShadowColor, computeOvertoneColor, ColorScheme } from '../theme/colorThemeContext';

const showOvertones = true;

type PianoKeyProps = {
  note: number;
  keyflash: boolean;
  isBlack: boolean;
  style?: React.CSSProperties;
  isActive: boolean;
  rootNote: number;
  rootPc: number | null;
  showTonicLabel: boolean;
  showScaleDegrees: boolean;
  lockedTonicNote: number | null;
  lockedTonicMode: number | 0;
  toggleNote: (note: number) => void;  // new prop
  activeNotes: number[];
  heldNotes: number[];
  toggledNotes: number[];
  keyCenter: number;
  mode: number;
  chordQual: number;
  csOptions: string[];
  activePcs: number[];
  sampler: Tone.Sampler | null;
  shouldPlaySound: boolean;
  // selectedInstrument: string;
  isScale: boolean;
  showOvertones: boolean;
  showNashvilleNumber: boolean;
  appTheme: string;
  tonicScalesNotes: number[][];
};

// Helper function to calculate the pitch class
const pitchClass = (note: number, rootNote: number = 0) => {
  return (note + 12 - rootNote) % 12;
};


// Old Overtone Logic
// const getOvertoneInfo = (note: number, activeNotes: number[]) => {
//   // Early return if the note itself is too high to be an overtone
//   if (note < 33 || note > 116) return false; // 33 is lowest possible overtone (A0+12), 116 is highest possible (Db6+31)

//   // Check if this note is an overtone of any active note
//   return activeNotes.some(activeNote => {
//     // Skip if the active note is too high to generate overtones
//     if (activeNote >= 86) return false; // D6 and up

//     // Skip if the active note is higher than or equal to the current note
//     if (activeNote >= note) return false;

//     // Calculate the interval between the notes
//     const interval = note - activeNote;

//     // Check if this interval matches any of the possible overtone intervals
//     // based on the range of the active note
//     if (activeNote >= 60 && activeNote <= 85) { // C4 to Db6
//       return interval === 12;
//     } else if (activeNote >= 47 && activeNote <= 59) { // B2 to B3
//       return interval === 12 || interval === 19;
//     } else if (activeNote >= 38 && activeNote <= 46) { // D2 to Bb2
//       return interval === 12 || interval === 19 || interval === 24;
//     } else if (activeNote >= 28 && activeNote <= 37) { // D1 to Db2
//       return interval === 12 || interval === 19 || interval === 24 || interval === 28;
//     } else if (activeNote >= 21 && activeNote <= 27) { // A0 to Db1
//       return interval === 12 || interval === 19 || interval === 24 || interval === 28 || interval === 31;
//     }

//     return false;
//   });
// };

const PianoKey: React.FC<PianoKeyProps> = ({
  note,
  keyflash,
  isBlack,
  style,
  isActive,
  rootNote,
  rootPc,
  showTonicLabel,
  showScaleDegrees,
  lockedTonicNote,
  lockedTonicMode,
  toggleNote,
  activeNotes,
  heldNotes,
  toggledNotes,
  keyCenter,
  mode,
  chordQual,
  csOptions,
  activePcs,
  sampler,
  shouldPlaySound,
  // selectedInstrument,
  isScale,
  showOvertones,
  showNashvilleNumber,
  appTheme,
  tonicScalesNotes
}) => {
  // const pc = pitchClass(note, showTonicLabel && lockedTonicNote ? lockedTonicNote : rootPc ? rootPc : rootNote);

  // FADE OUT OVERLAY, fix by moving to App.tsx to avoid endless loop
  const [showFadeOutOverlay, setShowFadeOutOverlay] = useState(false);
  const wasActiveRef = useRef(false); // Initialize to false explicitly
  const timerRef = useRef<NodeJS.Timeout | null>(null); // Reference to store the timeout

  const pc = pitchClass(note, lockedTonicNote !== null ? lockedTonicNote : rootPc !== null && isScale ? rootPc : rootNote);
  const notePc = note % 12;
  const prefix = isBlack ? 'black-' : '';
  // console.log("lockedTonicMode", lockedTonicMode)
  // console.log("######################### rootNote, chordQual", rootNote, chordQual)
  // console.log("activePcs", activePcs)
  const effectiveMode = lockedTonicNote !== null ? lockedTonicMode : chordQual;
  const pcClassNames = ['p-root', 'min-second', 'maj-second', 'min-third', 'maj-third', 'p-fourth', 'tritone', 'p-fifth', 'min-sixth', 'maj-sixth', 'min-seventh', 'maj-seventh'];
  const pcClass = pcClassNames[pc];

  const isOvertone = showOvertones && getOvertoneInfo(note, [...activeNotes, ...heldNotes, ...toggledNotes]);

  const overtoneOpacity = showOvertones
    ? getOvertoneInfo(note, [...activeNotes, ...heldNotes, ...toggledNotes])
    : false;

  const keyClassName = `key ${isBlack ? 'black' : 'white'} ${isActive ? 'active ' + prefix + pcClass : ''} ${keyflash ? 'keyflash' : ''} ${!isActive && isOvertone ? 'overtone overtone-pulse' : ''}`;


  // Begin FADE OUT OVERLAY, fixed endless loop by creating a timerRef to store the timeout
  useEffect(() => { // Track when a note becomes inactive to trigger fade-out overlay
    const thisNoteWasActive = activeNotes.includes(note) || heldNotes.includes(note) || toggledNotes.includes(note);

    if (wasActiveRef.current && !thisNoteWasActive) { // Only show fade-out if this specific note was active and now is not
      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setShowFadeOutOverlay(true);

      timerRef.current = setTimeout(() => { // Store the timer reference
        setShowFadeOutOverlay(false);
        timerRef.current = null;
      }, 500); // Match CSS animation duration (0.5s)
    }
    wasActiveRef.current = thisNoteWasActive; // Update ref to current active state

    if (thisNoteWasActive && showFadeOutOverlay) { // Cancel fadeout if the note becomes active again
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setShowFadeOutOverlay(false);
    }

    return () => { // Cleanup on unmount
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [activeNotes, heldNotes, toggledNotes, note]); // Remove showFadeOutOverlay from dependencies
  // End FADE OUT OVERLAY


  const handleKeyClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent click from bubbling to document

    if (shouldPlaySound && sampler && sampler.loaded) {
      // const noteName = midiToName(note, true);
      const noteName = Tone.Midi(note).toNote();
      if (toggledNotes.includes(note)) {
        // If the note is already toggled on, then we need to stop the sample.
        sampler.triggerRelease(noteName); // Stop the note sound
      } else {
        // If the note isn't toggled, then we need to play the sample.
        sampler.triggerAttack(noteName); // Play the note sound
      }
    }

    // Toggle the note's presence in the toggledNotes array
    toggleNote(note);
  };

  // Helper function to check for adjacent active notes
  const hasAdjacentNotes = () => {
    const allActiveNotes = [...activeNotes, ...heldNotes, ...toggledNotes];
    const adjacentHasOvertone = [-1, 1].some(offset => {
      const adjacentNote = note + offset;
      return getOvertoneInfo(adjacentNote, allActiveNotes) !== false;
    });
    return allActiveNotes.includes(note - 1) || allActiveNotes.includes(note + 1) || (showOvertones && adjacentHasOvertone);
    // return allActiveNotes.includes(note - 1) || allActiveNotes.includes(note + 1);
  };

  // Determine the vertical position based on adjacent notes
  const getNameDisplayStyle = () => {
    const baseStyle = {
      top: isBlack && hasAdjacentNotes() ? '-3.35dvw' : '-1.95dvw',
      color: appTheme === "light" ? 'rgb(0, 0, 0)' : 'rgb(221, 221, 221)',
      textShadow: appTheme === "light" ? '0.03dvw 0.03dvw 0.2dvw rgba(0, 0, 0, 0.1), 0.05dvw -0.03dvw 0.2dvw rgba(0, 0, 0, 0.1), -0.05dvw 0 rgba(0, 0, 0, 0.1)' :
        '0.03dvw 0.03dvw 0.2dvw rgb(0, 0, 0), 0.05dvw -0.03dvw 0.2dvw rgb(0, 0, 0), -0.05dvw 0 rgb(0, 0, 0)'
    };

    // If it's an overtone note, apply special styling
    if (!isActive && overtoneOpacity) {
      return {
        ...baseStyle,
        top: isBlack && hasAdjacentNotes() ? '-2.7dvw' : '-1.6dvw',
        opacity: 0.4,
        // opacity: Math.max(0.4, overtoneOpacity), // Use calculated opacity but ensure minimum visibility 
        fontStyle: 'italic',
        fontSize: '1.0dvw',    // Slightly smaller
        // fontWeight: '300',    // Lighter weight
        color: appTheme === "light" ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)', // Slightly different color
        // textShadow: '0 0 3px rgba(255, 255, 255, 0.3)' // Subtle glow
      };
    }

    // Return normal active note styling
    return baseStyle;
  };

  // const getNameDisplayStyle = () => {
  //   if (isBlack && hasAdjacentNotes()) {
  //     return { top: '-3.2dvw' };
  //   }
  //   return { top: '-1.8dvw' };
  // };

  const chordMode = chordQual == 2 ? 0 : chordQual == 3 ? 1 : chordQual == 4 ? 0 : chordQual;

  // console.log("rootNote", rootNote, "rootPc",rootPc, "mode", mode, "keyCenter", keyCenter, "note", note, "pc", pc, "lockedTonicNote", lockedTonicNote, "lockedTonicMode", lockedTonicMode, "chordQual", chordQual)
  // lockedTonicNote ? pc == rootPc ? console.log("midiToRomanNumeral", midiToRomanNumeral(note, lockedTonicNote ? lockedTonicNote : rootNote, mode)) : '' : ''

  // note == rootPc ? console.log("note == rootPc", pc, rootPc) : ''

  // !isBlack ? console.log('note:', note, 'pc:', pc, 'rootNote:', rootNote, 'rootPc:', rootPc) : ''

  const { colors } = useColors();

  const pcToColorKey: { [key: string]: string } = {
    'p-root': 'R',
    'min-second': 'm2',
    'maj-second': 'M2',
    'min-third': 'm3',
    'maj-third': 'M3',
    'p-fourth': 'P4',
    'tritone': 'd5',
    'p-fifth': 'P5',
    'min-sixth': 'm6',
    'maj-sixth': 'M6',
    'min-seventh': 'm7',
    'maj-seventh': 'M7'
  };

  const getBaseColor = (key: string) => {
    const colorKey = pcToColorKey[key];
    if (!colorKey || !colors[colorKey as keyof ColorScheme]) {
      console.warn(`No color found for key: ${key}, colorKey: ${colorKey}, mode: ${mode}, available colors:`, Object.keys(colors));
      return 'rgb(200, 200, 200)'; // fallback color
    }
    console.debug(`Getting color for key: ${key}, colorKey: ${colorKey}, mode: ${mode}, color:`, colors[colorKey as keyof ColorScheme][mode]);
    // console.log("THIS IS THE MODE!!!!!!!", mode);
    return colors[colorKey as keyof ColorScheme][lockedTonicNote !== null ? lockedTonicMode : mode];
  };

  const getShadowColor = (key: string) => {
    return computeShadowColor(getBaseColor(key));
  };

  const getBlackKeyColor = (key: string) => {
    return computeBlackKeyColor(getBaseColor(key));
  };

  const getBlackKeyShadowColor = (key: string) => {
    return computeBlackKeyShadowColor(getBaseColor(key));
  };

  // Old overtone function call
  // const getOvertoneColor = (key: string) => {
  //   return computeOvertoneColor(getBaseColor(key));
  // };

  const keyStyle: React.CSSProperties = {
    ...style,
    zIndex: isBlack ? 6 : isActive && (note % 12 === rootPc) ? 5 : 4, // Black notes on top, then white notes with Roman Numeral labels, then other white notes
    background: isActive ?
      `linear-gradient(${isBlack ? getBlackKeyShadowColor(pcClass) : getShadowColor(pcClass)}, 
       ${isBlack ? getBlackKeyColor(pcClass) : getBaseColor(pcClass)} 
       ${isBlack ? 'var(--black-shadow-location)' : 'var(--shadow-location)'})` :
      isBlack ? // default black and white key color set conditionally below:
        appTheme === "light" ? 'linear-gradient(180deg, rgb(15, 15, 15), rgb(40, 40, 40), rgb(50, 50, 50), rgb(45, 45, 45))' :
          appTheme === "dark" ? 'linear-gradient(180deg, rgb(5, 5, 5), rgb(20, 20, 20), rgb(30, 30, 30), rgb(25, 25, 25))' :
            'linear-gradient(180deg, rgb(15, 15, 15), rgb(30, 30, 30), rgb(40, 40, 40), rgb(35, 35, 35))' :
        appTheme === "light" ? 'linear-gradient(180deg, rgb(55, 55, 55), rgb(85, 85, 85),  rgb(170, 170, 170))' :
          appTheme === "dark" ? 'linear-gradient(180deg, rgb(45, 45, 45), rgb(50, 50, 50), rgb(120, 120, 120))' :
            'linear-gradient(180deg, rgb(55, 55, 55), rgb(70, 70, 70), rgb(120, 120, 120))',
    // !isActive && isOvertone ? getOvertoneColor(pcClass) :
    // undefined,
    // opacity: isActive ? 1 : isOvertone ? 0.9 : 1,
    borderTop: isBlack ? !isActive ? '0.04dvw solid rgba(20, 20, 20, 0.8)' : '0.04dvw 1dvw 1dvw 1dvw solid rgba(20, 20, 20, 0.8)' : '',
    borderBottom: isBlack ? !isActive ? '0.75dvw solid rgba(120, 120, 120, 0.1)' : '0.04dvw solid rgba(20, 20, 20, 0.8)' : '',
    borderLeft: isBlack ? !isActive ? '0.27dvw solid rgba(10, 10, 10, 0.7)' : '0.04dvw solid rgba(20, 20, 20, 0.8)' : '',
    borderRight: isBlack ? !isActive ? '0.27dvw solid rgba(10, 10, 10, 0.8)' : '0.04dvw solid rgba(20, 20, 20, 0.8)' : '',
    boxShadow: isBlack ? !isActive ? '0.1dvw 0.1dvw 0.3dvw 0.1dvw rgba(0, 0, 0, 0.1)' : '0.1dvw -0.1dvw 0.2dvw 0.0dvw rgba(0, 0, 0, 0.2)' : '',
    // outline: isOvertone ? '0.08dvw solid rgba(255, 255, 255, 1.0)' : '',
  };

  // Calculate styling of Roman Numeral
  const romanNumeral = lockedTonicNote !== null && midiToRomanNumeral(notePc, lockedTonicNote !== null ? lockedTonicNote : rootNote, getModeFromPcSet(tonicScalesNotes[0]), isScale ? mode : chordQual)
  const romanNumberalExtension = (chordQual == 2 ? '7' : chordQual == 3 ? '°' : chordQual == 4 ? '+' : '');
  const nashvilleNumber = lockedTonicNote !== null && midiToNashvilleNumber(notePc, lockedTonicNote !== null ? lockedTonicNote : rootNote, getModeFromPcSet(tonicScalesNotes[0]), isScale ? mode : chordQual)

  const romanLength = romanNumeral ? romanNumeral.length : 5;
  const paddingSize = 0.1 + ((5 - romanLength) / 4) * (0.2 - 0.19); // Now a number
  const fontAdjust = 0.0 + ((5 - romanLength) / 4) * (0.3 - 0.0); // Now a number
  const fontSize = isBlack
    ? mode == 1
      ? (1.2 - fontAdjust) : (1.0 - fontAdjust)
    : mode == 1
      ? (1.1 - fontAdjust) : (0.9 - fontAdjust)

  const paddingString = `${paddingSize.toFixed(3)}dvw`;
  const fontSizeString = `${fontSize.toFixed(3)}dvw`; // Convert to string for CSS
  // End of Roman Numeral styling

  const chordQualColorOpacity = 1.0;
  const chordQualColorMap = {
    0: `rgba(245, 229, 164, ${chordQualColorOpacity})`,  // Major yellow #f5e5a4
    1: `rgba(170, 221, 255, ${chordQualColorOpacity})`,  // Minor blue #aaddff
    2: `rgba(142, 237, 194, ${chordQualColorOpacity})`,  // Dominant green #8aedc2
    3: `rgba(142, 237, 194, ${chordQualColorOpacity})`,  // Diminished green #8aedc2
    4: `rgba(245, 203, 140, ${chordQualColorOpacity})`,  // Augmented orange #f5cb8c
  };

  const scaleDegreeStyle = lockedTonicNote !== null && notePc == rootPc // if key Center is locked, display Roman Numeral
    ? {
      ...(lockedTonicNote !== null && notePc === rootPc
        ? { // for Roman Numeral styling
          fontFamily: 'Bravura Text',
          // color: chordQualColorMap[chordQual],
          color: 'rgb(0, 0, 0, 1.0)',
          background: isActive ? 'rgb(255, 255, 255, 0.5)' : 'rgb(0, 0, 0, 0.3)',
          // background: chordQualColorMap[chordQual] || 'rgba(195, 195, 195, 0.9)',
          padding: paddingString,
          borderRadius: '0.2dvw',
          // marginBottom: isBlack ? '10.2dvw' : '13.2dvw',
          marginBottom: isBlack ? '1.0dvw' : '0.6dvw',
          boxShadow: '0.0dvw 0.1dvw 0.2dvw 0.2dvw rgb(0, 0, 0, 0.15)',
        }
        : ''),
      fontSize: '1.2dvw', // Use calculated font size here
      zIndex: 2,
    }
    : { fontSize: '0.75dvw' }; // if key Center is not locked, font size of scale degree text


  // console.log("lockedTonicNote", lockedTonicNote, "rootNote", rootNote, "rootPc", rootPc, "notePc", notePc, "note", note)

  // return ( <div title="Click a key to toggle on or off" className={keyClassName} style...

  return (
    <div className={keyClassName} style={keyStyle} onClick={handleKeyClick} data-actionable="true">
      {overtoneOpacity && (
        <div
          style={{
            position: 'absolute',
            ...(isBlack ? {
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            } : {
              top: 0,
              left: '20%',
              right: '20%',
              bottom: '3%',
            }),
            // background: computeOvertoneColor(getBaseColor(pcClass)),
            background: getBaseColor(pcClass),
            opacity: overtoneOpacity,
            pointerEvents: 'none', // Let clicks pass through
            // boxShadow: '0 0 0.5dvw 0.2dvw rgba(255, 255, 255, ' + overtoneOpacity + ')',
          }}
        />
      )}
      <div>
        <div className={`flash-overlay ${keyflash ? 'flash' : ''}`}></div>

        {/* FADE OUT OVERLAY, fix by moving to App.tsx to avoid endless loop */}
        <div className={`fade-out-overlay ${showFadeOutOverlay ? 'active' : ''}`}></div>

        {(isActive || overtoneOpacity) ? (
          <div className="name-display" style={getNameDisplayStyle()
            //   ...getNameDisplayStyle(),
            //   // opacity: isActive ? 1 : overtoneOpacity || 0,
            //   opacity: isActive ? 1 : 0.6,
            //   fontSize: isActive ? '1.5dvw' : '1.0dvw',
            //   verticalAlign: isActive ? '0.1dvw' : '0.9dvw'
            //   // fontStyle: isActive ? '' : 'italic'
            // }}>
          }>
            {/* {midiToName(note, false, rootNote ? rootNote : pc, mode)} */}
            {/* The line below uses spelling of key center */}
            {midiToName(note, false, lockedTonicNote !== null ? lockedTonicNote : rootPc ? rootPc : rootNote, lockedTonicNote !== null ? getModeFromPcSet(tonicScalesNotes[0]) : mode)}
            {/* The line below uses spelling of key center when not a scale */}
            {/* {midiToName(note, false, lockedTonicNote !== null && !isScale ? lockedTonicNote : rootPc ? rootPc : rootNote, lockedTonicNote !== null && !isScale ? lockedTonicMode : mode)} */}
            {/* The line below uses spelling of chord root */}
            {/* {midiToName(note, false, rootPc !== null ? rootPc : rootNote, chordMode)} */}
          </div>
        ) : null}
        {/* Use Below line for scale degree names */}
        {(isActive || overtoneOpacity) && showScaleDegrees ? (
          <div className="scale-degree-display" style={{
            ...scaleDegreeStyle,
            // opacity: isActive ? 1 : 0.97,
            color: isActive ? 'rgb(0, 0, 0)' : isBlack ? 'rgb(200, 200, 200)' : 'rgb(230, 230, 230)',
            // textShadow: isActive ? '0 0 0px rgba(0, 0, 0, 1.0)' : isBlack ? '0 0 0.5dvw rgba(0, 0, 0, 0.8)' : '0 0 0 rgba(0, 0, 0, 0)'  // Subtle glow
            fontStyle: isActive ? '' : 'italic'
          }}>
            {activePcs.length >= 1
              // ? lockedTonicNote !== null ? notePc == rootPc // use this line to show scale degrees related to chord root only when not locked to Key Center
              ? lockedTonicNote !== null && notePc == rootPc // use this line to show scale degree related to Key Center even when locked to Key Center
                ? <span>
                  {showNashvilleNumber ? <span style={{ fontSize: '1.15em' }}>{nashvilleNumber}</span> : romanNumeral}
                  {!isScale && isActive && showNashvilleNumber && chordQual === 1 && <span style={{ fontSize: '0.8em' }}>m</span>}
                  {!isScale && isActive && chordQual === 2 && !showNashvilleNumber && <sup style={{ fontSize: '0.8em' }}>7</sup>}
                  {!isScale && isActive && chordQual === 2 && showNashvilleNumber && <sup style={{ fontSize: '0.7em' }}>7</sup>}
                  {!isScale && isActive && chordQual === 3 && <span>°</span>}
                  {!isScale && isActive && chordQual === 4 && <sup style={{ fontSize: '0.8em' }}>+</sup>}
                </span>

                // : '' // comment out this line to show scale degrees related to Key Center
                : (/°/.test(csOptions && csOptions.length > 0 && csOptions[0] ? csOptions[0] : '') && (note - rootNote + 12) % 12 === 9)
                  ? '♭♭7'
                  : ((() => {
                    if (isScale) return true;
                    const cs = ((csOptions && csOptions.length > 0 && csOptions[0]) ? csOptions[0] : '').trim();
                    const hasExt = /(^|[^0-9])(7|9|11|13)([^0-9]|$)/.test(cs);  // handles C13, add9, b11, etc.
                    // const has69 = /(^|[^0-9])6\s*\/?\s*9([^0-9]|$)/.test(cs); // matches '69' or '6/9' even in 'C69', 'add6/9' // skipped for simpler 2, 6, 9 checks
                    if (lockedTonicNote !== null) return true;
                    if (cs === '') return true;           // Number
                    if (/2/.test(cs) && (note - rootNote + 12) % 12 === 2) return true;
                    if (/6/.test(cs) && (note - rootNote + 12) % 12 === 9) return true;      // Number for pc 9 in "69"
                    if (/9/.test(cs) && (note - rootNote + 12) % 12 === 2) return false;     // Extension for pc 2 in "69"
                    if (/4/.test(cs) && (note - rootNote + 12) % 12 === 5) return true;     // Number for pc 5 when "4" present
                    // if (/add\s*4/i.test(cs) && (note - rootNote + 12) % 12 === 5) return true;     // Number for pc 4 when add4 present
                    return !hasExt;                        // Number if no ext; else Extension
                  })())
                    ? lockedTonicNote !== null && (note - rootNote + 12) % 12 === 0
                      ? 'T'
                      : midiToScaleDegreeNumber(note, rootNote, lockedTonicNote !== null ? lockedTonicMode : chordQual)
                    : midiToScaleDegreeExtension(note, rootNote, lockedTonicNote !== null ? lockedTonicMode : chordQual)

              : null
            }
          </div>) : null}
      </div>
    </div >
  );
};

// export default React.memo(Key);
export default PianoKey;
